import { useQuery, useMutation, useQueryClient } from 'react-query'
import axios from 'lib/axiosConfig'
import {
  PlacesResponse,
  PlaceApi,
  PlaceDtsApi,
  PlaceRequest,
} from 'types/api/place'
import Place from 'types/model/place'
import { useAuth } from 'providers/AuthProvider'

const initialData = {
  country: '',
  group: '',
}

export const usePlaces = (
  page: number,
  pageSize: number,
  query: string | null
) => {
  const { userStores } = useAuth()
  const placesPayload = {
    page,
    page_size: pageSize,
    q: query,
    ...(!userStores.includes('any') && {
      master_ids: userStores,
    }),
  }
  const queryKey = { page, query }
  const {
    isLoading,
    error,
    data: response,
  } = useQuery(
    ['getPlaces', queryKey],
    async () => axios.get(`/v1/places`, { params: placesPayload }),
    { keepPreviousData: true }
  )

  const pagination = response?.data.meta.pagination
  const places: Place[] = response?.data.data.map((place: PlacesResponse) => ({
    ...initialData,
    ...place,
  }))

  if (query && !userStores.includes('any') && places) {
    const userPlaces = places.filter((place) =>
      userStores.includes(place.masterId)
    )
    return { isLoading, error, places: userPlaces, pagination }
  }

  return { isLoading, error, places, pagination }
}

export const usePlace = (placeId: string) => {
  const initPlaceData = {
    id: '',
    name: '',
    masterId: '',
    latitude: 0,
    longitude: 0,
    city: '',
    chain: { name: '' },
    searchable: false,
    extId: '',
    street: '',
    building: '',
    postcode: '',
    country: {
      code: '',
      name: '',
    },
    email: '',
    phones: [{ number: '' }],
  }

  const {
    isLoading,
    error,
    data: response,
  } = useQuery(
    ['getPlace', placeId],
    async () => axios.get(`/v1/places/${placeId}`),
    {
      keepPreviousData: true,
    }
  )
  const place: Place = { ...initPlaceData, ...response?.data.data }
  return { isLoading, error, place }
}

export const useGetUserPlaces = (
  placeIds: string[],
  query?: string | null,
  enabledCondition?: string
) => {
  const queryKey = query ? placeIds.join() + query : placeIds.join()
  const params = {
    ...(query &&
      query.length > 2 && {
        q: query,
      }),
    ...(placeIds[0] !== 'any' && {
      master_ids: placeIds,
    }),
  }
  const {
    data: response,
    isLoading,
    error,
  } = useQuery(
    ['getUserPlaces', queryKey],
    async () =>
      axios.get<PlacesResponse>(`/v1/places`, {
        params,
      }),
    {
      enabled: enabledCondition
        ? enabledCondition === 'true'
        : placeIds && placeIds.length > 0,
    }
  )
  const places = response
    ? response.data.data
        ?.map((q: PlaceApi) => {
          return (
            {
              label: q.name,
              value: q.masterId,
            } || []
          )
        })
        .filter((p: { label: string; value: string }) => p.label)
    : []

  if (query && !placeIds.includes('any') && places.length > 0) {
    const userPlaces = places.filter((place) => placeIds.includes(place?.value))
    return { isLoading, places: userPlaces, error }
  }

  return { isLoading, places, error }
}

export const usePatchPlace = (placeId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: Partial<PlaceRequest>) =>
      axios.patch(`/v1/places/${placeId}`, values),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getPlace', placeId])
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['getPlace', placeId])
        queryClient.invalidateQueries(['getExtData', placeId])
      },
    }
  )
}

const usePatchPlaceOnCreation = () => {
  return useMutation((masterId: string) => {
    const dtsValues = {
      // this value will change driver_uses_shopkeeper
      receives_delivery_updates: true,
      masterId,
      store_guid: masterId,
    }
    return axios.patch(`/v1/dts/places/${masterId}`, dtsValues)
  })
}

export const usePostPlace = () => {
  const { mutateAsync } = usePatchPlaceOnCreation()
  return useMutation(
    (values: Partial<PlaceApi>) => axios.post(`/v1/places`, values),
    {
      onSuccess: async (data) => {
        mutateAsync(data.data.data.masterId)
      },
    }
  )
}

export const useGetPlaceName = (placeId: string) => {
  const {
    isLoading,
    error,
    data: response,
  } = useQuery(
    ['getPlaceName', placeId],
    async () => axios.get(`/v1/places/${placeId}`),
    {
      keepPreviousData: true,
      enabled: !!placeId,
    }
  )
  const placeName = !isLoading && !error ? response?.data.data.name : ''
  return { placeName }
}

export const useGetDtsPlace = (placeMasterId: string) => {
  const initSupportContactData = {
    supportEmail: '',
    supportPhone: '',
    email: '',
    activeForOrdering: false,
    sendDeliveryEstimates: false,
    usesShopkeeper: false,
    deliveryStore: false,
    collectionStore: false,
  }
  const {
    isLoading,
    error,
    data: response,
  } = useQuery(
    ['getDtsPlace', placeMasterId],
    async () => axios.get(`/v1/dts/places/${placeMasterId}`),
    {
      keepPreviousData: true,
      enabled: !!placeMasterId,
    }
  )
  const dtsPlace = {
    ...initSupportContactData,
    ...response?.data.data,
  }
  return { isLoading, error, dtsPlace }
}

// for patch requests to this endpoint, store_guid is required
export const usePatchDtsPlace = (placeMasterId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: Partial<PlaceDtsApi>) =>
      axios.patch(`/v1/dts/places/${placeMasterId}`, values),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getDtsPlace', placeMasterId])
      },
      onSuccess: () => {
        queryClient.fetchQuery(['getDtsPlace', placeMasterId])
      },
    }
  )
}
